var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { defaultHeaderInterpreter } from '../header/interpreter';
import { CacheRequestInterceptor } from '../interceptors/request';
import { CacheResponseInterceptor } from '../interceptors/response';
import { MemoryAxiosStorage } from '../storage/memory';
import { defaultKeyGenerator } from '../util/key-generator';
/**
 * Apply the caching interceptors for a already created axios instance.
 *
 * @example
 *
 * ```ts
 * import Axios from 'axios';
 * import { setupCache, AxiosCacheInstance } from 'axios-cache-interceptor';
 *
 * // instance will have our custom typings from the return of this function
 * const instance = setupCache(
 *   Axios.create({
 *     // Axios options
 *   }),
 *   {
 *     // Axios-cache-interceptor options
 *   }
 * );
 *
 * // OR
 *
 * const instance = axios.create({
 *   // Axios options
 * }) as AxiosCacheInstance;
 *
 * // As this functions returns the same axios instance but only with
 * // different typings, you can ignore the function return.
 * setupCache(instance, {
 *   // Axios-cache-interceptor options
 * });
 * ```
 *
 * @param axios The already created axios instance
 * @param config The config for the caching interceptors
 * @returns The same instance with better typescript types.
 */
export function setupCache(axios, _a) {
    if (_a === void 0) { _a = {}; }
    var storage = _a.storage, generateKey = _a.generateKey, waiting = _a.waiting, headerInterpreter = _a.headerInterpreter, requestInterceptor = _a.requestInterceptor, responseInterceptor = _a.responseInterceptor, cacheOptions = __rest(_a, ["storage", "generateKey", "waiting", "headerInterpreter", "requestInterceptor", "responseInterceptor"]);
    var axiosCache = axios;
    axiosCache.storage = storage || new MemoryAxiosStorage();
    axiosCache.generateKey = generateKey || defaultKeyGenerator;
    axiosCache.waiting = waiting || {};
    axiosCache.headerInterpreter = headerInterpreter || defaultHeaderInterpreter;
    axiosCache.requestInterceptor =
        requestInterceptor || new CacheRequestInterceptor(axiosCache);
    axiosCache.responseInterceptor =
        responseInterceptor || new CacheResponseInterceptor(axiosCache);
    // CacheRequestConfig values
    axiosCache.defaults = __assign(__assign({}, axios.defaults), { cache: __assign({ ttl: 1000 * 60 * 5, interpretHeader: false, methods: ['get'], cachePredicate: { statusCheck: [200, 399] }, etag: false, modifiedSince: false, update: {} }, cacheOptions) });
    // Apply interceptors
    axiosCache.requestInterceptor.use();
    axiosCache.responseInterceptor.use();
    return axiosCache;
}
/** @deprecated */
export var useCache = setupCache;
/** @deprecated */
export var createCache = setupCache;
